import { useEffect, useState, type FormEvent } from "react";
import { Check, Copy, KeyRound, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductKeyMetadata, ProductKeyReplacement } from "@/lib/generated/model";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { containsProductKey } from "@/lib/product-key-redaction";
import { formatMaskedProductKey } from "@/lib/product-key-display";

export function createReplacementIdempotencyKey() {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto?.getRandomValues?.(new Uint32Array(4));
  return bytes ? Array.from(bytes, (value) => value.toString(36)).join("") : `replace-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type ProductKeyReplacementAttempt = {
  keyID: string;
  reason: string;
  idempotencyKey: string;
  submitted?: boolean;
};

export function ReplaceProductKeyDialog({
  open,
  onOpenChange,
  keyID,
  busy,
  error,
  canDiscard = false,
  requestedKeyID,
  replacement,
  attempt,
  onAttemptChange,
  onReplace,
  onFinish,
  onDiscard,
  onResolveTargetMismatch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyID: string;
  busy: boolean;
  error: string | null;
  canDiscard?: boolean;
  requestedKeyID?: string | null;
  replacement: ProductKeyReplacement | null;
  attempt: ProductKeyReplacementAttempt | null;
  onAttemptChange: (attempt: ProductKeyReplacementAttempt) => void;
  onReplace: (reason: string, idempotencyKey: string) => void;
  onFinish: () => void;
  onDiscard?: () => void;
  onResolveTargetMismatch?: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const hasTargetMismatch = Boolean(requestedKeyID && requestedKeyID !== keyID);

  useEffect(() => {
    if (!open) return;
    setReason(attempt?.keyID === keyID ? attempt.reason : "");
    setValidation(null);
    setCopied(false);
    setCopyError(null);
    setCopyConfirmed(false);
    setDiscardConfirm(false);
  }, [attempt, keyID, open]);

  function close(nextOpen: boolean) {
    if (!nextOpen && (busy || replacement)) return;
    if (!nextOpen) {
      setReason("");
      setValidation(null);
    }
    onOpenChange(nextOpen);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reason.trim()) {
      setValidation(t("dialogs.replaceProductKeyReasonRequired", "A reason is required to replace this Product Key."));
      requestAnimationFrame(() => document.getElementById("replace-product-key-reason")?.focus());
      return;
    }
    if (containsProductKey(reason)) {
      setValidation(t("dialogs.replaceProductKeyReasonContainsKey", "Reason must not contain a Product Key."));
      return;
    }
    setValidation(null);
    const nextAttempt = attempt?.keyID === keyID
      ? { ...attempt, reason: reason.trim() }
      : { keyID, reason: reason.trim(), idempotencyKey: createReplacementIdempotencyKey() };
    onAttemptChange(nextAttempt);
    onReplace(nextAttempt.reason, nextAttempt.idempotencyKey);
  }

  async function copyKey() {
    if (!replacement) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(replacement.product_key);
      setCopied(true);
      setCopyError(null);
    } catch {
      setCopied(false);
      setCopyError(t("dialogs.replaceProductKeyCopyFailed", "Copy failed. Select the Product Key below and copy it manually before finishing."));
    }
  }

  function finish() {
    if (!copyConfirmed) return;
    onFinish();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" showClose={!busy && !replacement}>
        {replacement ? (
          <div>
            <DialogHeader>
              <div className="flex items-center gap-2 text-success">
                <KeyRound className="size-5" aria-hidden="true" />
                <DialogTitle className="text-base font-semibold text-ink">
                  {t("dialogs.replaceProductKeySuccessTitle", "Replacement Product Key ready")}
                </DialogTitle>
              </div>
              <DialogDescription className="mt-2 text-xs leading-5 text-muted">
                {t("dialogs.replaceProductKeyRevealNotice", "Copy and store this new Product Key now. This delivery view stays open until you confirm the copy.")}
              </DialogDescription>
            </DialogHeader>
            <section className="mt-4 rounded-md border border-warning/35 bg-warning/10 p-3" aria-label={t("dialogs.replaceProductKeySecret", "Replacement Product Key")}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-warning">{t("dialogs.replaceProductKeySecret", "Replacement Product Key")}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => void copyKey()}>
                  {copied ? <Check className="size-3.5 text-success" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
                  {copied ? t("common.copied", "Copied to clipboard") : t("dialogs.copyProductKey", "Copy Product Key")}
                </Button>
              </div>
              {copyError && <p className="mt-2 text-xs text-warning" role="alert">{copyError}</p>}
              <Input className="mt-3 font-mono text-xs" aria-label={t("dialogs.replaceProductKeySecret", "Replacement Product Key")} readOnly value={replacement.product_key} onFocus={(event) => event.currentTarget.select()} />
            </section>
            <ReplacementEvidence replacement={replacement} />
            <label className="mt-4 flex items-start gap-2 text-sm text-ink" htmlFor="replace-product-key-copied-confirm">
              <input id="replace-product-key-copied-confirm" className="mt-0.5 size-4 accent-ink" type="checkbox" checked={copyConfirmed} onChange={(event) => setCopyConfirmed(event.target.checked)} />
              <span>{t("dialogs.replaceProductKeyCopiedConfirm", "I copied the new Product Key to an approved secret system.")}</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="primary" size="sm" disabled={!copyConfirmed} onClick={finish}>
                {t("dialogs.finish", "Finish")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <RefreshCw className="size-5 text-muted" aria-hidden="true" />
                <DialogTitle className="text-base font-semibold text-ink">{t("dialogs.replaceProductKeyTitle", "Replace Product Key")}</DialogTitle>
              </div>
              <DialogDescription className="mt-2 text-xs leading-5 text-muted">
				{t("dialogs.replaceProductKeyConfirm", "The old Product Key becomes terminal. Its linked active Device, if any, will be deactivated, and the replacement Key is available for a new Activation.")}
                <span className="mt-1 block font-mono text-ink">{keyID}</span>
              </DialogDescription>
            </DialogHeader>
            <p className="mt-3 text-xs leading-5 text-muted">
              {t(
                "dialogs.deactivateActivationOfflineConsequence",
                "This is not an immediate remote kill. A signed offline token may continue to work until its bounded expiry or offline window ends.",
              )}
            </p>
            <div className="mt-4 space-y-2">
              <Label htmlFor="replace-product-key-reason" className="text-xs">{t("dialogs.reason", "Reason")} <span className="text-danger">*</span></Label>
              <Input id="replace-product-key-reason" value={reason} onChange={(event) => { const nextReason = event.target.value; setReason(nextReason); onAttemptChange(attempt?.keyID === keyID ? { ...attempt, reason: nextReason } : { keyID, reason: nextReason, idempotencyKey: createReplacementIdempotencyKey() }); }} aria-invalid={Boolean(validation || error)} aria-describedby={validation || error ? "replace-product-key-reason-error" : undefined} autoFocus disabled={busy || attempt?.submitted} />
            </div>
            {(validation || error) && <div id="replace-product-key-reason-error" className="mt-3"><InlineAlert title={t("dialogs.replaceProductKeyFailed", "Product Key replacement could not complete")} message={validation || error!} variant="error" /></div>}
            {hasTargetMismatch && (
              <div className="mt-3">
                <InlineAlert
                  title={t("dialogs.replaceProductKeyTargetMismatchTitle", { defaultValue: "Resolve {{currentKeyID}} before replacing {{requestedKeyID}}", currentKeyID: keyID, requestedKeyID })}
                  message={t("dialogs.replaceProductKeyTargetMismatchMessage", "This dialog has not switched targets. Continue the saved recovery attempt or discard it after a definitive rejection.")}
                  variant="info"
                />
              </div>
            )}
            {discardConfirm && (
              <div className="mt-3">
                <InlineAlert
                  title={t("dialogs.replaceProductKeyDiscardTitle", "Discard this recovery attempt?")}
                  message={t("dialogs.replaceProductKeyDiscardMessage", "Discarding removes the saved retry handle. Only continue after a definitive API rejection.")}
                  variant="info"
                />
              </div>
            )}
            <DialogFooter>
              {discardConfirm ? (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={() => setDiscardConfirm(false)}>{t("dialogs.replaceProductKeyKeepAttempt", "Keep attempt")}</Button>
                  <Button type="button" variant="danger" size="sm" onClick={onDiscard}>{t("dialogs.replaceProductKeyDiscardConfirm", "Discard and close")}</Button>
                </>
              ) : hasTargetMismatch ? (
                <>
                  {canDiscard && <Button type="button" variant="danger" size="sm" onClick={() => setDiscardConfirm(true)}>{t("dialogs.replaceProductKeyDiscard", "Discard attempt")}</Button>}
                  <Button type="button" variant="outline" size="sm" onClick={() => close(false)}>{t("common.cancel", "Cancel")}</Button>
                  <Button type="button" variant="primary" size="sm" onClick={onResolveTargetMismatch}>{t("dialogs.replaceProductKeyContinueRecovery", { defaultValue: "Continue recovery for {{keyID}}", keyID })}</Button>
                </>
              ) : (
                <>
                  {canDiscard && <Button type="button" variant="danger" size="sm" onClick={() => setDiscardConfirm(true)}>{t("dialogs.replaceProductKeyDiscard", "Discard attempt")}</Button>}
                  <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => close(false)}>{t("common.cancel", "Cancel")}</Button>
                  <Button type="submit" variant="primary" size="sm" disabled={busy || !reason.trim()}>
                    {busy ? <BusyLabel>{t("dialogs.replacing", "Replacing")}</BusyLabel> : t("dialogs.replaceProductKey", "Replace Product Key")}
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReplacementEvidence({ replacement }: { replacement: ProductKeyReplacement }) {
  const { t } = useTranslation();
  const metadata: Array<[string, ProductKeyMetadata]> = [
    [t("dialogs.previousProductKey", "Previous Product Key"), replacement.old_key],
    [t("dialogs.newProductKey", "New Product Key"), replacement.new_key],
  ];
  return <section className="mt-4 grid gap-3 border-y border-line py-4 sm:grid-cols-2" aria-label={t("dialogs.replaceProductKeyEvidence", "Product Key replacement evidence")}>{metadata.map(([label, key]) => <div key={label} className="min-w-0"><p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">{label}</p><dl className="mt-1 space-y-1 text-xs"><div className="flex justify-between gap-2"><dt className="text-muted">{t("common.productKey", "Product Key")}</dt><dd className="font-mono text-ink">{formatMaskedProductKey(key.masked_prefix, key.masked_suffix)}</dd></div><div className="flex justify-between gap-2"><dt className="text-muted">{t("dialogs.status", "Status")}</dt><dd className="text-ink">{key.status}</dd></div><div className="flex justify-between gap-2"><dt className="text-muted">{t("dialogs.batch", "Batch")}</dt><dd className="max-w-28 truncate font-mono text-ink" title={key.batch_id}>{key.batch_id}</dd></div></dl></div>)}</section>;
}
